<template>
  <div class="xy-vertical-layout" :style="layoutStyle" @scroll="debouncedOnScroll">
    <xyComponent v-for="element in props.element.nodes" :element="element" :key="element.uuid" :language="props.language" :list-index="props.listIndex" :list-item-data="props.listItemData" />
  </div>
</template>

<script setup>
import { computed, nextTick, reactive, onBeforeMount } from 'vue'
import { info, defaultProps } from './config'
import xyComponent from '@/packages/components/xy-component.vue'
import useListDataSource from '@/h5/hooks/useListDataSource'
import useComponentRef from '@/packages/plugins/hook/useComponent'
import { debounce } from 'lodash'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)
const layoutStyle = computed(() => {
  if (!props.scroll) {
    return {
      height: 'auto',
      overflowY: 'hidden'
    }
  } else {
    return {
      overflowY: 'scroll'
    }
  }
})
const state = reactive({
  scrollPosition: 0,
  isLoading: false
})

const { getList, initListData } = useListDataSource()
const { getComponentRef } = useComponentRef()

const loadMore = async () => {
  const { componentUUID, funcName } = props.pullupConfig || {}
  if (componentUUID && funcName) {
    const el = getComponentRef(componentUUID)
    if (el?.[funcName]) {
      await el[funcName]()
    }
    return
  }
  if (props.pullupSource) {
    await getList(props.pullupSource)
  }
}

const onScroll = async (event) => {
  if (!props.pullup || state.isLoading) return
  const hasPullupTarget = (props.pullupConfig?.componentUUID && props.pullupConfig?.funcName) || props.pullupSource
  if (!hasPullupTarget) return

  const { scrollTop, clientHeight, scrollHeight } = event.target
  const threshold = 12
  if (clientHeight + scrollTop >= scrollHeight - threshold) {
    state.isLoading = true
    state.scrollPosition = scrollTop

    try {
      await loadMore()
      nextTick(() => {
        event.target.scrollTop = state.scrollPosition
      })
    } finally {
      state.isLoading = false
    }
  }
}

const debouncedOnScroll = debounce(onScroll, 200)

onBeforeMount(() => {
  if (props.pullup && props.pullupSource && !props.pullupConfig?.componentUUID) {
    initListData(props.pullupSource)
  }
})
</script>

<style lang="scss" scoped>
.xy-vertical-layout {
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.xy-vertical-layout::-webkit-scrollbar {
  display: none;
}
</style>
